/**
 * CajaPageSkeleton — Esqueleto de la pantalla principal de caja (abierta o cerrada).
 * Usado en CajaPage (datos) y en Suspense al cargar el chunk de la ruta.
 */
import { Skeleton } from "@/components/ui";
import CenteredStatePanel from "@/components/common/CenteredStatePanel";
import { txReadRaw } from "@/lib/persistence/transactionalStore";

export type CajaPageSkeletonVariant = "open" | "closed" | "auto";

function resolveVariant(variant: CajaPageSkeletonVariant): "open" | "closed" {
  if (variant !== "auto") return variant;
  try {
    return txReadRaw("caja_last_status") === "open" ? "open" : "closed";
  } catch {
    return "closed";
  }
}

function CajaOpenSkeleton() {
  return (
    <div
      className="flex flex-col h-full gap-4 lg:gap-6 p-2 lg:p-0 animate-fadeIn"
      role="status"
      aria-busy="true"
      aria-label="Cargando caja"
    >
      <div className="flex flex-col lg:flex-row flex-1 gap-4 lg:gap-6 overflow-hidden pb-16 lg:pb-0 min-h-[50vh]">
        <div className="flex-1 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden h-full min-h-[280px]">
          <div className="p-3 lg:p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 flex flex-col gap-2">
            <div className="flex gap-2 sm:gap-4 items-center justify-between">
              <Skeleton
                width="w-full"
                height="h-9"
                rounded="lg"
                className="max-w-xs sm:max-w-md"
              />
              <div className="flex gap-2 shrink-0">
                <Skeleton width="w-20" height="h-9" rounded="lg" />
                <Skeleton width="w-28" height="h-9" rounded="lg" />
              </div>
            </div>
          </div>
          <div className="flex-1 p-3 lg:p-4 space-y-5 bg-gray-50/30 dark:bg-slate-900/50 overflow-hidden">
            {[1, 2].map((cat) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton width="w-24" height="h-7" rounded="lg" />
                  <Skeleton width="w-5" height="h-4" rounded="md" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2 lg:gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl p-3 space-y-2"
                    >
                      <div className="flex items-center gap-2">
                        <Skeleton width="w-8" height="h-8" rounded="lg" />
                        <div className="flex-1 space-y-1">
                          <Skeleton width="w-3/4" height="h-3.5" rounded="md" />
                          <Skeleton width="w-16" height="h-4" rounded="md" />
                        </div>
                      </div>
                      <Skeleton width="w-20" height="h-2.5" rounded="md" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden lg:flex w-96 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 flex-col h-full min-h-[280px]">
          <div className="p-5 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 rounded-t-xl">
            <Skeleton width="w-36" height="h-6" rounded="md" />
          </div>
          <div className="flex-1 p-4 flex flex-col items-center justify-center gap-3">
            <Skeleton
              width="w-12"
              height="h-12"
              rounded="full"
              className="opacity-20"
            />
            <Skeleton width="w-32" height="h-4" rounded="md" />
          </div>
          <div className="p-4 border-t border-gray-100 dark:border-slate-700 space-y-3">
            <div className="flex justify-between items-center">
              <Skeleton width="w-24" height="h-5" rounded="md" />
              <Skeleton width="w-16" height="h-6" rounded="md" />
            </div>
            <Skeleton width="w-full" height="h-12" rounded="lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CajaClosedSkeleton() {
  return (
    <CenteredStatePanel maxWidthClass="max-w-md">
      <div
        className="flex flex-col items-center gap-6 w-full animate-fadeIn"
        role="status"
        aria-busy="true"
        aria-label="Cargando caja"
      >
        <div className="bg-gray-100 dark:bg-slate-800 p-8 rounded-full">
          <Skeleton width="w-16" height="h-16" rounded="full" />
        </div>
        <div className="text-center w-full space-y-3">
          <Skeleton
            width="w-48"
            height="h-7"
            rounded="md"
            className="mx-auto"
          />
          <Skeleton width="w-full" height="h-4" rounded="md" />
          <Skeleton
            width="w-4/5"
            height="h-4"
            rounded="md"
            className="mx-auto"
          />
        </div>
        <div className="flex flex-col gap-4 w-full">
          <Skeleton width="w-full" height="h-12" rounded="lg" />
          <Skeleton width="w-full" height="h-12" rounded="lg" />
        </div>
      </div>
    </CenteredStatePanel>
  );
}

export function CajaPageSkeleton({
  variant = "auto",
}: {
  variant?: CajaPageSkeletonVariant;
}) {
  return resolveVariant(variant) === "open" ? (
    <CajaOpenSkeleton />
  ) : (
    <CajaClosedSkeleton />
  );
}
